import { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';

import './StatPill.css';
import './StatSliderPill.css';

const MIN_VISIBLE_PERCENTAGE = 2;

const formatPercentage = (value) => (value > 0 && value < 1 ? value.toFixed(2) : Math.round(value));

const StatSliderPill = forwardRef(({ label, value, percentage, tooltip, negative, minWidth, className, style, ...rest }, ref) => {
    const rawPercentage = Math.max(0, Number(percentage) || 0);

    const [displayPercentage, setDisplayPercentage] = useState(0);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setDisplayPercentage(rawPercentage));
        return () => cancelAnimationFrame(frame);
    }, [rawPercentage]);

    const visiblePercentage = displayPercentage > 0 && displayPercentage < MIN_VISIBLE_PERCENTAGE
        ? MIN_VISIBLE_PERCENTAGE
        : displayPercentage;

    let normalWidth;
    let overflowWidth;
    if (negative) {
        normalWidth = Math.min(100, visiblePercentage);
        overflowWidth = 0;
    } else {
        const scale = Math.max(100, rawPercentage);
        const combinedWidth = Math.min(100, (visiblePercentage / scale) * 100);
        const normalBoundaryWidth = (100 / scale) * 100;
        normalWidth = Math.min(combinedWidth, normalBoundaryWidth);
        overflowWidth = Math.max(0, combinedWidth - normalBoundaryWidth);
    }
    const isOverLimit = !negative && rawPercentage > 100;

    return (
        <div ref={ref} className={`statSliderPill ${className || ''}`} style={{ minWidth, ...style }} {...rest}>
            <div className="statSliderPillHeader">
                <span className="statPillLabel">{label}</span>
                <span className="statPillValue">
                    {value}
                    <span className={`statSliderPillPercentage ${isOverLimit ? 'statSliderPillPercentage--over' : ''}`}>
                        {negative ? '-' : ''}{formatPercentage(rawPercentage)}%
                    </span>
                    {tooltip &&
                        <OverlayTrigger placement="top" overlay={<Tooltip id={`stat-slider-tooltip-${uuid()}`}>{tooltip}</Tooltip>}>
                            <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: 0.5 }} aria-hidden="true"></i>
                        </OverlayTrigger>
                    }
                </span>
            </div>
            <div className="statSliderPillTrack">
                <div
                    className={`statSliderPillFill ${negative ? 'statSliderPillFill--negative' : ''}`}
                    style={{ width: `${normalWidth}%` }}
                />
                {isOverLimit &&
                    <div className="statSliderPillFill statSliderPillFill--overflow" style={{ width: `${overflowWidth}%` }} />
                }
            </div>
        </div>
    );
});

StatSliderPill.displayName = 'StatSliderPill';

StatSliderPill.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node.isRequired,
    percentage: PropTypes.number,
    tooltip: PropTypes.node,
    negative: PropTypes.bool,
    minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    style: PropTypes.object,
};

StatSliderPill.defaultProps = {
    percentage: 0,
    negative: false,
    minWidth: 210,
};

export default StatSliderPill;
